import { useState, type ReactElement } from 'react';
import type { MCPServerListItem } from '@kirin/api-client/contracts/mcp';
import { Button } from '@/components/ui/button';
import { BlockToggle } from '@/components/shared/BlockToggle';
import type { GatewayControlCallbacks } from './gatewayControlUtils';

/** Mirrors UNAVAILABLE_REASON in GatewayControlCell.tsx, scoped to one tool. */
const TOOL_UNAVAILABLE_REASON =
  'Blocking gateway tools is not available yet. This shows what the gateway reports; changing it needs a Kirin release that can send the policy.';

const VISIBLE_TOOLS_LIMIT = 4;

interface GatewayToolListExpansionProps {
  row: MCPServerListItem;
  callbacks: GatewayControlCallbacks;
}

/**
 * Row-expansion content for a gateway-managed server (KIR-3940 AC 4).
 * Per-tool toggle renders `effectiveBlocked` — a tool under a blocked server
 * shows Blocked even though its own `blocked` bit is untouched (cascade).
 * The toggle is also disabled while the server block is in effect: flipping
 * the tool's own `blocked` bit could not change `effectiveBlocked` while the
 * server-level cascade still applies, so the control would otherwise look
 * unresponsive.
 */
export function GatewayToolListExpansion({
  row,
  callbacks,
}: GatewayToolListExpansionProps): ReactElement {
  const [showAll, setShowAll] = useState(false);
  const tools = row.tools ?? [];
  const visibleTools = showAll ? tools : tools.slice(0, VISIBLE_TOOLS_LIMIT);
  const serverId = row.id ?? row.name;

  return (
    <div className="flex flex-col gap-1">
      <ul className="divide-y">
        {visibleTools.map(tool => {
          const toolId = tool.id ?? tool.name;
          return (
            <li
              key={toolId}
              className="flex items-center justify-between gap-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="font-mono text-sm font-medium truncate">
                  {tool.name}
                </div>
                {tool.description && (
                  <p className="text-xs text-muted-foreground truncate">
                    {tool.description}
                  </p>
                )}
              </div>
              {/* Disabled for the same reason as the server-level switch
                  (KIR-5042): there is no gateway policy route, and a toggle
                  that moves on screen while no server hears about it is worse
                  than one that says it is not ready. The server-block
                  inheritance message still wins when it applies — that one is
                  about THIS data rather than about Kirin. */}
              <span
                title={
                  row.blocked
                    ? 'This tool is blocked because the server is blocked. Lift the server-level block to control it individually.'
                    : TOOL_UNAVAILABLE_REASON
                }
              >
                <BlockToggle
                  blocked={tool.effectiveBlocked ?? false}
                  name={tool.name}
                  onAllow={() => callbacks.onAllowTool(serverId, toolId)}
                  onRequestBlock={() =>
                    callbacks.onRequestBlockConfirmation({
                      kind: 'tool',
                      serverId,
                      toolId,
                      toolName: tool.name,
                    })
                  }
                  disabled
                />
              </span>
            </li>
          );
        })}
      </ul>
      {!showAll && tools.length > VISIBLE_TOOLS_LIMIT && (
        <Button
          variant="link"
          size="sm"
          className="self-start"
          onClick={() => setShowAll(true)}
        >
          Show all {tools.length} tools
        </Button>
      )}
    </div>
  );
}
